import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });

      if (response.data.access_token) {
        localStorage.setItem('admin_token', response.data.access_token);
        toast.success('Login successful!');
        navigate('/admin');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Login · B&B Magazine</title>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold text-primary mb-2">B&B MAG</h1>
            <p className="text-muted-foreground">Admin Panel</p>
          </div>

          <Card className="bg-[hsl(var(--surface-2))] border-border">
            <CardHeader>
              <CardTitle className="font-display">Login</CardTitle>
              <CardDescription>Enter your credentials to access the admin panel</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-ui">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@bbmag.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="admin-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-ui">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="admin-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full font-ui" 
                  disabled={loading}
                  data-testid="admin-login-button"
                >
                  {loading ? (
                    <span>Logging in...</span>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Login
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-4">
            <a href="/" className="hover:text-primary transition-colors duration-200">
              ← Back to website
            </a>
          </p>
        </div>
      </div>
    </>
  );
}